import React, {
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react'
import { CircularProgress, styled } from '@mui/material'

import {
    getCognitoUser,
    getSession,
    i18Messages,
    Language,
    logout,
} from '@focaltec-suite/api'
import ExternApp from './ExternApp'
import { AppContext } from './AppContext'
import { useLocation } from 'react-router-dom'
import ProductSelection from '../products/ProductSelection'
import {
    Product,
    products,
    getProductByRedirectUrl,
} from '../products/Products'
import { FeedbackContext } from '@focaltec-suite/commons'
import { initTranslator, translate } from '@focaltec-suite/core'

const Container = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
}))

type Status = 'unknown' | 'out' | 'in' | 'product_selection'

export default function SessionHolder() {
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)

    const qs = useLocation().search
    const query = useMemo(() => new URLSearchParams(qs), [qs])
    const origin = useMemo(() => query.get('continue') ?? '', [query])
    const product = useMemo(
        () => (origin ? getProductByRedirectUrl(origin) : undefined),
        [origin]
    )

    const [status, setStatus] = useState<Status>('unknown')

    context.fixPath = (path: string) => {
        return `${path}?continue=${origin}`
    }

    const handleLogout = (message: string) => {
        logout(() => {
            context.product = undefined
            feedbackContext.closeBackdrop()
            feedbackContext.showError(message)
            setStatus('out')
        })
    }

    const i18nPromise = useCallback(async (lang: Language) => {
        return await i18Messages(lang)
    }, [])

    context.onSignedIn = () => {
        getSession()
            .then((session) => {
                if (session.accesses.length > 0) {
                    const validAccess = session.accesses.find((access) => {
                        return (
                            access.apps.find(
                                (app) => app.id === product?.id
                            ) !== undefined
                        )
                    })

                    if (validAccess) {
                        window.location.href = origin
                    } else {
                        const firstProduct = session.accesses
                            .map((access) => {
                                return products.find((prod) => {
                                    return (
                                        access.apps.find(
                                            (app) => app.id === prod.id
                                        ) !== undefined
                                    )
                                })
                            })
                            .find((el) => !!el)

                        context.product = firstProduct
                        if (firstProduct) {
                            window.location.href = firstProduct.redirectUrl
                        } else {
                            handleLogout(translate('without_apps') as string)
                        }
                    }
                } else {
                    handleLogout(translate('without_accesses') as string)
                }
            })
            .catch((error) => {
                handleLogout(error.message)
            })
    }

    useEffect(() => {
        let lang: Language = 'es'
        if (navigator.language.startsWith('en')) {
            lang = 'en'
        }

        i18nPromise(lang)
            .then((texts) => {
                initTranslator({
                    lang: lang,
                    messages: texts,
                })

                return getCognitoUser()
            })
            .then((session) => {
                context.onSignedIn()
            })
            .catch((error) => {
                if (origin) {
                    setStatus('out')
                } else {
                    console.error(error)
                    context.product = undefined
                    setStatus('product_selection')
                }
            })
    }, [context, origin, i18nPromise])

    const onProductSelected = (product: Product) => {
        context.product = product
        window.location.href = `${process.env.REACT_APP_ID_URL}?continue=${product.redirectUrl}`
    }

    if (status === 'product_selection') {
        return <ProductSelection onSelect={onProductSelected} />
    }

    if (status === 'out') {
        return <ExternApp />
    }

    return (
        <Container>
            <CircularProgress
                sx={(theme) => {
                    return { margin: theme.spacing(4) }
                }}
            />
        </Container>
    )
}
